/**
 * Table column drag-and-drop reordering.
 *
 * Opt-in per table by setting a few data attributes in the template:
 *
 *   <table data-table-key="office.jobs"
 *          data-column-reorder="1"
 *          data-column-order="customer,location,worksite,service,...">
 *     <thead>
 *       <tr>
 *         <th data-column-key="customer">...</th>
 *         <th data-column-key="location">...</th>
 *         ...
 *       </tr>
 *     </thead>
 *     <tbody>
 *       <tr>
 *         <td data-column-key="name">...</td>
 *         <td data-column-key="duration">...</td>
 *       </tr>
 *       ...
 *     </tbody>
 *   </table>
 *
 * - Reads `data-column-order` once on init and applies that order to the
 *   already-rendered DOM (no flash of default order).
 * - Lets the user drag any <th> to a new position; on drop, the
 *   corresponding <td>s in every <tbody> row are reshuffled in the same
 *   order. A <th> marked `data-column-fixed="true"` stays put.
 * - Persists the new order server-side via POST /office/api/table-prefs/<key>/
 *   (fire-and-forget; failures are logged, never shown to the user).
 * - HTMX-aware: re-inits after `htmx:afterSettle`.
 */

import Sortable from 'sortablejs';

const API_URL = '/office/api/table-prefs/';

function getCsrfToken() {
    const el = document.querySelector('[name=csrfmiddlewaretoken]');
    if (el && el.value) return el.value;
    const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
    return m ? m[1] : '';
}

function readHeaderOrder(table) {
    const row = table.tHead && table.tHead.rows[0];
    if (!row) return [];
    return Array.from(row.cells)
        .map(th => th.dataset.columnKey)
        .filter(Boolean);
}

function reorderRowCells(tr, order) {
    const cellsByKey = {};
    Array.from(tr.cells).forEach(td => {
        const key = td.dataset.columnKey;
        if (key) cellsByKey[key] = td;
    });
    order.forEach(key => {
        const td = cellsByKey[key];
        if (td) tr.appendChild(td);
    });
}

// Reconcile a user's saved column order with the table's current template
// order. Two drift cases must be handled so a stale saved layout never
// corrupts the rendering:
//   1. A saved key no longer exists in the template (column removed) — drop it.
//   2. A template key is missing from the saved order (column ADDED to the
//      template after the user saved a layout) — insert it at its natural
//      template position, NOT at the front/back. Without this, a freshly
//      added column drifts to the very first slot the next time the saved
//      layout is applied.
function mergeOrderWithDom(savedOrder, domOrder) {
    const merged = savedOrder.filter(key => domOrder.includes(key));
    domOrder.forEach((key, idx) => {
        if (!merged.includes(key)) {
            merged.splice(Math.min(idx, merged.length), 0, key);
        }
    });
    return merged;
}

function applyColumnOrder(table, order) {
    if (!Array.isArray(order) || order.length === 0) return;
    const thead = table.tHead;
    if (!thead || !thead.rows[0]) return;
    const headerRow = thead.rows[0];

    // Build a full order that contains EVERY current column exactly once,
    // so appending in this order deterministically places all of them
    // (new columns included) instead of stranding unknown keys at the front.
    const finalOrder = mergeOrderWithDom(order, readHeaderOrder(table));

    const thByKey = {};
    Array.from(headerRow.cells).forEach(th => {
        const key = th.dataset.columnKey;
        if (key) thByKey[key] = th;
    });

    finalOrder.forEach(key => {
        const th = thByKey[key];
        if (th) headerRow.appendChild(th);
    });

    Array.from(table.tBodies).forEach(tbody => {
        Array.from(tbody.rows).forEach(tr => reorderRowCells(tr, finalOrder));
    });
}

function persistOrder(tableKey, order) {
    try {
        return fetch(API_URL + encodeURIComponent(tableKey) + '/', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCsrfToken(),
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ column_order: order }),
        }).catch(err => console.warn('[tableReorder] save failed:', err));
    } catch (err) {
        console.warn('[tableReorder] save threw:', err);
    }
}

function initTable(table) {
    if (!table || table.dataset.reorderInit === '1') return;
    const tableKey = table.dataset.tableKey;
    if (!tableKey) return;

    table.dataset.reorderInit = '1';

    const savedCsv = (table.dataset.columnOrder || '').trim();
    if (savedCsv) {
        applyColumnOrder(
            table,
            savedCsv.split(',').map(s => s.trim()).filter(Boolean),
        );
    }

    const headerRow = table.tHead && table.tHead.rows[0];
    if (!headerRow) return;

    Sortable.create(headerRow, {
        animation: 150,
        draggable: 'th',
        filter: '[data-column-fixed="true"]',
        preventOnFilter: false,
        // Use the mouse-events fallback instead of the native HTML5 drag API
        // so we get full CSS control over the floating ghost (compact chip
        // with a move icon) without the OS hijacking the cursor.
        forceFallback: true,
        fallbackTolerance: 3,
        ghostClass: 'col-reorder-ghost',
        chosenClass: 'col-reorder-chosen',
        dragClass: 'col-reorder-drag',
        onStart: function(evt) {
            // Compute where the user clicked inside the original <th>, then
            // use that as a margin shift on the floating clone so the
            // cursor ends up anchored at the chip's top-left corner
            // regardless of where the drag was initiated from.
            const oe = evt.originalEvent || {};
            const touch = oe.touches && oe.touches[0];
            const clientX = oe.clientX != null ? oe.clientX : (touch ? touch.clientX : 0);
            const clientY = oe.clientY != null ? oe.clientY : (touch ? touch.clientY : 0);
            const rect = evt.item.getBoundingClientRect();
            const offsetX = Math.max(0, clientX - rect.left);
            const offsetY = Math.max(0, clientY - rect.top);
            document.documentElement.style.setProperty('--col-drag-shift-x', offsetX + 'px');
            document.documentElement.style.setProperty('--col-drag-shift-y', offsetY + 'px');
        },
        onEnd: function() {
            document.documentElement.style.removeProperty('--col-drag-shift-x');
            document.documentElement.style.removeProperty('--col-drag-shift-y');
            const newOrder = readHeaderOrder(table);
            Array.from(table.tBodies).forEach(tbody => {
                Array.from(tbody.rows).forEach(tr => reorderRowCells(tr, newOrder));
            });
            persistOrder(tableKey, newOrder);
        },
    });
}

export function initReorderableTables(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope
        .querySelectorAll('table[data-table-key][data-column-reorder="1"]')
        .forEach(initTable);
}

function initAll() {
    // Always scan the full document. The dataset.reorderInit guard in
    // initTable makes this idempotent, and scanning globally protects
    // against swap events whose `target` detail doesn't contain the
    // freshly-injected table (hx-boost navigation, inline script swaps,
    // etc).
    initReorderableTables(document);
    // Clear any drag-shift vars left over from an aborted drag
    // (e.g. navigating away mid-drag) so stale values don't affect
    // the next drag operation.
    document.documentElement.style.removeProperty('--col-drag-shift-x');
    document.documentElement.style.removeProperty('--col-drag-shift-y');
}

document.addEventListener('DOMContentLoaded', initAll);
document.addEventListener('htmx:afterSettle', initAll);
document.addEventListener('htmx:load', initAll);
window.addEventListener('pageshow', function(e) {
    // Fires on normal load AND when the browser restores the page from the
    // back/forward cache — in which case DOMContentLoaded does NOT fire.
    if (e.persisted) initAll();
});

window.initReorderableTables = initReorderableTables;
